var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, query as firestoreQuery, where, setDoc, Timestamp, updateDoc, getFirestore } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
// Collection names
const VENDORS_COLLECTION = 'vendors';
const USER_VENDOR_ACCESS_COLLECTION = 'user_vendor_access';
/**
 * Hook for vendor-related Firebase queries and mutations using React Query
 */
export const useVendorQueries = () => {
    const { currentUser } = useAuth();
    const queryClient = useQueryClient();
    const db = getFirestore();
    // Query for getting all vendors associated with the current user
    const useVendorsByUser = () => {
        return useQuery({
            queryKey: ['vendors', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    return [];
                // Find all vendor access entries for this user
                const accessRef = collection(db, USER_VENDOR_ACCESS_COLLECTION);
                const q = firestoreQuery(accessRef, where('userId', '==', currentUser.uid));
                const querySnapshot = yield getDocs(q);
                // Fetch the associated vendor profiles
                const vendors = [];
                for (const accessDoc of querySnapshot.docs) {
                    const accessData = accessDoc.data();
                    const vendorRef = doc(db, VENDORS_COLLECTION, accessData.vendorId);
                    const vendorDoc = yield getDoc(vendorRef);
                    if (vendorDoc.exists()) {
                        vendors.push(vendorDoc.data());
                    }
                }
                return vendors;
            }),
            enabled: !!currentUser
        });
    };
    // Query for getting a specific vendor by ID
    const useVendorById = (vendorId) => {
        return useQuery({
            queryKey: ['vendor', vendorId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!vendorId)
                    return null;
                const vendorRef = doc(db, VENDORS_COLLECTION, vendorId);
                const vendorDoc = yield getDoc(vendorRef);
                if (vendorDoc.exists()) {
                    return vendorDoc.data();
                }
                return null;
            }),
            enabled: !!vendorId
        });
    };
    // Query to check if user has access to any vendor
    const useHasVendorAccess = () => {
        return useQuery({
            queryKey: ['hasVendorAccess', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    return false;
                const accessRef = collection(db, USER_VENDOR_ACCESS_COLLECTION);
                const q = firestoreQuery(accessRef, where('userId', '==', currentUser.uid));
                const querySnapshot = yield getDocs(q);
                const hasAccess = !querySnapshot.empty;
                return hasAccess;
            }),
            enabled: !!currentUser
        });
    };
    // Query to get user's role for a specific vendor
    const useVendorRole = (vendorId) => {
        return useQuery({
            queryKey: ['vendorRole', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid, vendorId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser || !vendorId)
                    return null;
                const accessId = `${currentUser.uid}_${vendorId}`;
                const accessRef = doc(db, USER_VENDOR_ACCESS_COLLECTION, accessId);
                const accessDoc = yield getDoc(accessRef);
                if (accessDoc.exists()) {
                    return accessDoc.data().role;
                }
                return null;
            }),
            enabled: !!currentUser && !!vendorId
        });
    };
    // Mutation for creating a new vendor profile
    const useCreateVendor = () => {
        return useMutation({
            mutationFn: (data) => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser) {
                    throw new Error('You must be logged in to register a vendor');
                }
                console.log('[CreateVendor] Начало создания вендора...');
                // Create a new vendor document with auto-generated ID
                const vendorRef = doc(collection(db, VENDORS_COLLECTION));
                const vendorId = vendorRef.id;
                const now = Timestamp.now();
                console.log('[CreateVendor] Подготовка данных вендора:', data.companyName);
                // Copy registration data to vendor profile data
                const vendorData = Object.assign(Object.assign({ id: vendorId }, data), { status: 'active', createdAt: now.toDate(), updatedAt: now.toDate() });
                // Save the vendor profile
                console.log('[CreateVendor] Сохранение профиля вендора...');
                yield setDoc(vendorRef, vendorData);
                console.log('[CreateVendor] Профиль вендора успешно сохранен');
                // Create the user-vendor access relationship
                const accessId = `${currentUser.uid}_${vendorId}`;
                const accessData = {
                    id: accessId,
                    userId: currentUser.uid,
                    vendorId,
                    role: 'owner',
                    createdAt: now.toDate()
                };
                console.log('[CreateVendor] Сохранение данных связи...');
                yield setDoc(doc(db, USER_VENDOR_ACCESS_COLLECTION, accessId), accessData);
                console.log('[CreateVendor] Данные связи успешно сохранены');
                console.log('[CreateVendor] Вендор успешно создан:', vendorData.companyName);
                return vendorData;
            }),
            onSuccess: () => {
                // Invalidate the vendor list query to trigger a refetch
                queryClient.invalidateQueries({ queryKey: ['vendors', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid] });
                queryClient.invalidateQueries({ queryKey: ['hasVendorAccess', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid] });
            }
        });
    };
    // Mutation for updating a vendor profile
    const useUpdateVendor = () => {
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ vendorId, data }) {
                const vendorRef = doc(db, VENDORS_COLLECTION, vendorId);
                const updateData = Object.assign(Object.assign({}, data), { updatedAt: Timestamp.now().toDate() });
                yield updateDoc(vendorRef, updateData);
                return { vendorId, data: updateData };
            }),
            onSuccess: (result) => {
                // Invalidate specific vendor query
                queryClient.invalidateQueries({ queryKey: ['vendor', result.vendorId] });
                queryClient.invalidateQueries({ queryKey: ['vendors', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid] });
            }
        });
    };
    // Mutation for adding a user to a vendor
    const useAddUserToVendor = () => {
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ userId, vendorId, role = 'user' }) {
                const accessId = `${userId}_${vendorId}`;
                const accessRef = doc(db, USER_VENDOR_ACCESS_COLLECTION, accessId);
                // Check if the relationship already exists
                const accessDoc = yield getDoc(accessRef);
                if (accessDoc.exists()) {
                    throw new Error('User already has access to this vendor');
                }
                const now = Timestamp.now();
                const accessData = {
                    id: accessId,
                    userId,
                    vendorId,
                    role,
                    createdAt: now.toDate()
                };
                yield setDoc(accessRef, accessData);
                return accessData;
            })
        });
    };
    return {
        useVendorsByUser,
        useVendorById,
        useHasVendorAccess,
        useVendorRole,
        useCreateVendor,
        useUpdateVendor,
        useAddUserToVendor
    };
};
